import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import AudioControlsButton from 'components/common/AudioControlsButton';
import styles from './index.module.scss';

// 畫筆模式下 ExtendedContentCanvas 的 .canvasWrapper（z-index: 100）會蓋住整個內容區。
// 而 .tabPanel 一旦被 fitPanelToContainer 套上 transform: scale()，就成為獨立的 stacking
// context，panel 內的 z-index 再高也出不來 —— 音檔按鈕的 z-index: 110 因此完全無效。
//
// 這裡的作法與 .controlsButton（中文顯示鈕）同一個路子：把「可點擊的部分」放到 .tabPanel
// 之外。差別是音檔按鈕的位置跟著內文流動、又會隨 panel 一起縮放，搬不走本體，所以只把一塊
// 透明的點擊代理 portal 到 layer 上，按鈕本體留在原位 —— 視覺、縮放、播放狀態都不變。
const AudioHitLayerContext = React.createContext(null);

// 供 PortalAudioControlsButton 掛載點擊代理的圖層。
// version 改變時所有代理會重新量測位置，由外層在版面（縮放／分頁）定案後遞增。
export const AudioHitLayer = ({ version = 0, children }) => {
  const [layer, setLayer] = useState(null);

  const value = useMemo(function () {
    return { layer, version };
  }, [layer, version]);

  return (
    <AudioHitLayerContext.Provider value={value}>
      {children}
      <div ref={setLayer} className={styles.audioHitLayer} />
    </AudioHitLayerContext.Provider>
  );
};

export const PortalAudioControlsButton = ({ className, src, onPlaying, onEnded, onClick }) => {
  const context = useContext(AudioHitLayerContext);
  const buttonRef = useRef(null);
  const [box, setBox] = useState(null);

  const layer = context ? context.layer : null;
  const version = context ? context.version : 0;

  useEffect(function () {
    const button = buttonRef.current;
    if (!layer || !button) return;

    const measure = function () {
      const layerRect = layer.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();

      // 小視窗本身也可能被 transform: scale()（ExtendedContentContainer 的 popWindowScale），
      // 兩個 rect 拿到的都是縮放後的視覺座標；代理是 layer 的子節點、會再吃一次同樣的縮放，
      // 所以要先除回 layer 的本地座標，位置才不會被縮第二次。
      const scale = layer.offsetWidth > 0 ? layerRect.width / layer.offsetWidth : 0;

      // 補充／一字多義分頁時未顯示的項目是 display: none，量到的尺寸為 0；
      // 此時不能留下代理，否則版面上會多出一塊看不見卻擋得住畫筆的區域。
      if (!scale || buttonRect.width === 0 || buttonRect.height === 0) {
        setBox(null);
        return;
      }

      setBox({
        left: (buttonRect.left - layerRect.left) / scale,
        top: (buttonRect.top - layerRect.top) / scale,
        width: buttonRect.width / scale,
        height: buttonRect.height / scale
      });
    };

    // fitPanelToContainer 會在同一個 frame 內二分搜尋比例、反覆改 transform，
    // 等下一個 frame 版面定案後再量，才不會量到中間過程的尺寸
    const rafId = requestAnimationFrame(measure);
    return function () {
      cancelAnimationFrame(rafId);
    };
  }, [layer, version, src]);

  const handleProxyClick = useCallback(function (e) {
    // 代理是 div，冒泡到 ExtendedContentContainer 會被 clearCanvas 以 class 名稱比對而誤清畫記；
    // 原本點到的是 <button>（clearCanvas 只處理 div），行為必須保持一致
    e.stopPropagation();
    if (buttonRef.current) buttonRef.current.click();
  }, []);

  return (
    <>
      <AudioControlsButton
        ref={buttonRef}
        className={className}
        src={src}
        onPlaying={onPlaying}
        onEnded={onEnded}
        onClick={onClick}
      />
      {layer && box && ReactDOM.createPortal(
        <div
          className={styles.hitProxy}
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height
          }}
          onClick={handleProxyClick}
        />,
        layer
      )}
    </>
  );
};
